import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { categoryService, productService } from '../services/api';
import {
  getCategorySlug,
  findCategoryForStorefrontPath,
  getCategoryDepth,
  getParentCategoryId,
  productBelongsToCategoryId,
} from '../utils/categoryUtils';
import ProductCard from '../components/ProductCard';
import SiteLoading from '../components/SiteLoading';
import { placeholderDataUrl } from '../utils/placeholderImage';
import { getPrimaryProductImageUrl } from '../utils/productImages';
import {
  filterProductsByTag,
  formatTagPageTitle,
  resolveTagRouteContext,
  tagQueryCandidates,
} from '../utils/productTags';
import './CategoryPage.css';
import '../pages/ProductPage.css'; // Import ProductPage CSS for scrolling section styles

/** Backend allows limit 1–100; paginate to load full catalog for /category/all */
const PRODUCTS_PAGE_LIMIT = 100;

async function fetchAllProductsPaged() {
  const merged = [];
  let page = 1;
  const maxPages = 50;
  while (page <= maxPages) {
    const productsResponse = await productService.getProducts({
      page,
      limit: PRODUCTS_PAGE_LIMIT,
      sort: 'createdAt',
      order: 'desc',
    });
    const list = Array.isArray(productsResponse.products)
      ? productsResponse.products
      : Array.isArray(productsResponse.products?.data)
      ? productsResponse.products.data
      : [];
    merged.push(...list);
    if (list.length < PRODUCTS_PAGE_LIMIT) break;
    page += 1;
  }
  return merged;
}

function normalizeProductsList(productsResponse) {
  if (!productsResponse?.success || !productsResponse.products) return [];
  const raw = productsResponse.products;
  return Array.isArray(raw) ? raw : Array.isArray(raw.data) ? raw.data : [];
}

/**
 * Many products store the main category in `category` and the leaf (sub/child) id in `subcategory`.
 * Backend `?category=<id>` often only matches `category`, so subcategory pages return empty — fallback filters client-side.
 */
async function fetchProductsForCategoryPage(categoryId, parentCategoryId) {
  const cid = String(categoryId).trim();
  const list = normalizeProductsList(
    await productService.getProducts({
      category: cid,
      limit: 100,
      sort: 'createdAt',
      order: 'desc',
    })
  );
  if (list.length > 0) return list;

  const pid = parentCategoryId != null ? String(parentCategoryId).trim() : '';
  if (pid) {
    const fromParent = normalizeProductsList(
      await productService.getProducts({
        category: pid,
        limit: 100,
        sort: 'createdAt',
        order: 'desc',
      })
    );
    const filtered = fromParent.filter((p) => productBelongsToCategoryId(p, cid));
    if (filtered.length > 0) return filtered;
  }

  const all = await fetchAllProductsPaged();
  return all.filter((p) => productBelongsToCategoryId(p, cid));
}

function formatCategoryLoadError(err) {
  const raw = err?.message || 'Failed to load category';
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.error === 'string') return parsed.error;
  } catch {
    /* plain string */
  }
  return raw;
}

function toSearchableText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value.toLowerCase();
  if (Array.isArray(value)) return value.map((v) => toSearchableText(v)).join(' ');
  if (typeof value === 'object') return Object.values(value).map((v) => toSearchableText(v)).join(' ');
  return String(value).toLowerCase();
}

function productMatchesSearch(product, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    product?.name,
    product?.description,
    product?.shortDescription,
    product?.brand,
    product?.material,
    product?.fabric,
    product?.tags,
    product?.colors,
    product?.specifications,
  ]
    .map((v) => toSearchableText(v))
    .join(' ');
  return haystack.includes(q);
}

const CategoryPage = () => {
  const { category, subcategory, tag, brand } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [categoryData, setCategoryData] = useState(null);
  const [products, setProducts] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [childrenCategories, setChildrenCategories] = useState([]);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [categoryLevel, setCategoryLevel] = useState(null); // 0 = main, 1 = subcategory, 2 = children
  const searchParam = new URLSearchParams(location.search).get('q') || '';
  
  // Main products list pagination (mobile = 12, desktop = 24 per page)
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth <= 768 ? 12 : 24;
  });

  // Scrolling products section state (from ProductPage)
  const [currentRecommendedGroup, setCurrentRecommendedGroup] = useState(0);
  const [recommendedGroupSize, setRecommendedGroupSize] = useState(() => {
    return window.innerWidth <= 768 ? 2 : 4;
  });
  const isRecommendedMobileView = recommendedGroupSize === 2;
  const relatedSectionClassName = `professional-related-section ${
    isRecommendedMobileView ? 'professional-related-section--mobile' : 'professional-related-section--desktop'
  }`;
  const relatedContainerClassName = `professional-container ${
    isRecommendedMobileView ? 'professional-container--mobile' : 'professional-container--desktop'
  }`;
  const relatedGridClassName = `related-products-grid ${
    isRecommendedMobileView ? 'related-products-grid--ymal-mobile' : 'related-products-grid--ymal-desktop'
  }`;

  useEffect(() => {
    const loadCategoryData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Search route: /search?q=...
        if (searchParam.trim()) {
          const query = searchParam.trim();
          const virtualCategory = {
            _id: `search-${query}`,
            name: `Search results`,
            description: `Showing products matching "${query}"`,
            image: '',
            level: 2,
          };
          setCategoryData(virtualCategory);
          setCategoryLevel(2);
          setSubcategories([]);
          setChildrenCategories([]);

          const allProducts = await fetchAllProductsPaged();
          const matched = allProducts.filter((p) => productMatchesSearch(p, query));
          setProducts(matched);

          if (matched.length === 0) {
            const recRes = await productService.getProducts({ limit: 8, sort: 'createdAt', order: 'desc' });
            if (recRes.success && recRes.products) {
              const recList = Array.isArray(recRes.products) ? recRes.products : recRes.products?.data || [];
              setRecommendedProducts(recList.slice(0, 8));
            }
          } else {
            setRecommendedProducts([]);
          }
          setLoading(false);
          return;
        }

        // Tag-only route: /tag/:tag - products whose tags[] include this label (strict match)
        if (tag && !category) {
          const tagLabelFromState = location.state?.tagLabel;
          const { displayName } = resolveTagRouteContext(tag, { tagLabel: tagLabelFromState });
          const virtualCategory = {
            _id: tag,
            name: formatTagPageTitle(displayName),
            description: `Products tagged with "${formatTagPageTitle(displayName)}"`,
            image: '',
            level: 2,
          };
          setCategoryData(virtualCategory);
          setCategoryLevel(2);
          setSubcategories([]);
          setChildrenCategories([]);

          let productsList = [];
          for (const queryTag of tagQueryCandidates(tag, tagLabelFromState)) {
            const productsResponse = await productService.getProducts({
              tag: queryTag,
              limit: 100,
              sort: 'createdAt',
              order: 'desc',
            });
            const raw = normalizeProductsList(productsResponse);
            const filtered = filterProductsByTag(raw, tag, tagLabelFromState);
            if (filtered.length > 0) {
              productsList = filtered;
              break;
            }
          }

          if (productsList.length === 0) {
            const all = await fetchAllProductsPaged();
            productsList = filterProductsByTag(all, tag, tagLabelFromState);
          }

          setProducts(productsList);
          if (productsList.length === 0) {
            const recRes = await productService.getProducts({ limit: 8, sort: 'createdAt', order: 'desc' });
            if (recRes.success && recRes.products) {
              const recList = Array.isArray(recRes.products) ? recRes.products : recRes.products?.data || [];
              setRecommendedProducts(recList.slice(0, 8));
            }
          } else {
            setRecommendedProducts([]);
          }
          setLoading(false);
          return;
        }

        // Brand-only route: /brand/:brand - load products by brand
        if (brand) {
          const brandDisplayName = brand.replace(/-/g, ' ');
          const virtualCategory = {
            _id: brand,
            name: brandDisplayName.charAt(0).toUpperCase() + brandDisplayName.slice(1),
            description: `Products by brand "${brandDisplayName}"`,
            image: '',
            level: 2
          };
          setCategoryData(virtualCategory);
          setCategoryLevel(2);
          setSubcategories([]);
          setChildrenCategories([]);
          // Pass both slug and display name - backend may use case-insensitive match
          const productsResponse = await productService.getProducts({ brand: brandDisplayName });
          if (productsResponse.success && productsResponse.products) {
            const productsList = Array.isArray(productsResponse.products)
              ? productsResponse.products
              : Array.isArray(productsResponse.products?.data)
              ? productsResponse.products.data
              : [];
            setProducts(productsList);
            if (productsList.length === 0) {
              const recRes = await productService.getProducts({ limit: 8, sort: 'createdAt', order: 'desc' });
              if (recRes.success && recRes.products) {
                const recList = Array.isArray(recRes.products) ? recRes.products : recRes.products?.data || [];
                setRecommendedProducts(recList.slice(0, 8));
              }
            }
          } else {
            setProducts([]);
          }
          setLoading(false);
          return;
        }

        // All products: /category/all
        if (category === 'all' && !subcategory && !tag) {
          const virtualCategory = {
            _id: 'all',
            name: 'All Products',
            description: 'Browse our full collection',
            image: '',
            level: 2
          };
          setCategoryData(virtualCategory);
          setCategoryLevel(2);
          setSubcategories([]);
          setChildrenCategories([]);
          const productsList = await fetchAllProductsPaged();
          setProducts(productsList);
          if (productsList.length === 0) {
            const recRes = await productService.getProducts({ limit: 8, sort: 'createdAt', order: 'desc' });
            if (recRes.success && recRes.products) {
              const recList = Array.isArray(recRes.products) ? recRes.products : recRes.products?.data || [];
              setRecommendedProducts(recList.slice(0, 8));
            }
          }
          setLoading(false);
          return;
        }

        // Check if this is a special page (new-in or on-sale)
        const isNewInPage = category === 'new-in' || category === 'newin';
        const isSalePage = category === 'on-sale' || category === 'sale' || category === 'on-sale';

        // For special pages, load products by tag directly
        if (isNewInPage || isSalePage) {
          const tagToFilter = isNewInPage ? 'newin' : 'sale';
          
          // Create a virtual category object for display
          const virtualCategory = {
            _id: tagToFilter,
            name: isNewInPage ? 'New In' : 'On Sale',
            description: isNewInPage 
              ? 'Discover our latest arrivals and newest additions to the collection' 
              : 'Shop our amazing sale items with great discounts',
            image: '',
            level: 2
          };
          
          setCategoryData(virtualCategory);
          setCategoryLevel(2);
          setSubcategories([]);
          setChildrenCategories([]);
          
          // Load products by tag
          const productsResponse = await productService.getProducts({ tag: tagToFilter });
          if (productsResponse.success && productsResponse.products) {
            const productsList = Array.isArray(productsResponse.products) 
              ? productsResponse.products 
              : Array.isArray(productsResponse.products.data) 
              ? productsResponse.products.data 
              : [];
          setProducts(productsList);
          
          // Load recommended products if no products found
          if (productsList.length === 0) {
            const recommendedResponse = await productService.getProducts({ limit: 8, sort: 'createdAt', order: 'desc' });
            if (recommendedResponse.success && recommendedResponse.products) {
              const recommendedList = Array.isArray(recommendedResponse.products) 
                ? recommendedResponse.products 
                : Array.isArray(recommendedResponse.products.data) 
                ? recommendedResponse.products.data 
                : [];
              setRecommendedProducts(recommendedList.slice(0, 8));
            }
          }
        } else {
          setProducts([]);
        }
          
          setLoading(false);
          return;
        }

        const categoriesResponse = await categoryService.getCategories();
        let categoriesData = [];
        
        if (categoriesResponse.success && categoriesResponse.categories) {
          if (Array.isArray(categoriesResponse.categories)) {
            categoriesData = categoriesResponse.categories;
          } else if (Array.isArray(categoriesResponse.categories.data)) {
            categoriesData = categoriesResponse.categories.data;
          }
        } else if (Array.isArray(categoriesResponse)) {
          categoriesData = categoriesResponse;
        }
        
        setAllCategories(categoriesData);

        const categorySlug = subcategory && tag 
          ? `${category}/${subcategory}/${tag}` 
          : subcategory 
          ? `${category}/${subcategory}` 
          : category;

        const foundCategory = findCategoryForStorefrontPath(categoriesData, categorySlug);

        if (foundCategory) {
          setCategoryData(foundCategory);
          const categoryId = foundCategory._id || foundCategory.id;
          
          const level = getCategoryDepth(foundCategory, categoriesData);
          setCategoryLevel(level);
          const parentCategoryIdForProducts = getParentCategoryId(foundCategory);
          
          // Helper function to get parent ID
          const getParentId = (cat) => {
            if (cat.parentCategory) {
              if (typeof cat.parentCategory === 'object' && cat.parentCategory._id) {
                return cat.parentCategory._id;
              } else if (typeof cat.parentCategory === 'object') {
                return cat.parentCategory.id || cat.parentCategory;
              } else {
                return cat.parentCategory;
              }
            } else if (cat.parent) {
              if (typeof cat.parent === 'object' && cat.parent._id) {
                return cat.parent._id;
              } else if (typeof cat.parent === 'object') {
                return cat.parent.id || cat.parent;
              } else {
                return cat.parent;
              }
            }
            return null;
          };
          
          // Load subcategories and children categories based on level
          if (level === 0) {
            // Main category - get subcategories and children categories
            const categoryIdString = String(categoryId).trim();
            const subcats = categoriesData.filter(cat => {
              const parentId = getParentId(cat);
              if (!parentId) return false;
              return String(parentId).trim() === categoryIdString;
            });
            setSubcategories(subcats);
            
            // Get children categories (children of subcategories)
            const children = [];
            subcats.forEach(subcat => {
              const subcatId = subcat._id || subcat.id;
              const subcatIdString = String(subcatId).trim();
              const childCats = categoriesData.filter(cat => {
                const parentId = getParentId(cat);
                if (!parentId) return false;
                return String(parentId).trim() === subcatIdString;
              });
              children.push(...childCats);
            });
            setChildrenCategories(children);
            
            // Load products for main category (with subcategory field fallback for APIs that only index `category`)
            const productsList = await fetchProductsForCategoryPage(categoryId, parentCategoryIdForProducts);
            setProducts(productsList);
            
            // Load recommended products for main categories and subcategories pages
            // Always load for level 0 and level 1, or if no products/subcategories/children found
            if (level === 0 || level === 1 || (productsList.length === 0 && subcats.length === 0 && children.length === 0)) {
              const recommendedResponse = await productService.getProducts({ limit: 8, sort: 'createdAt', order: 'desc' });
              if (recommendedResponse.success && recommendedResponse.products) {
                const recommendedList = Array.isArray(recommendedResponse.products) 
                  ? recommendedResponse.products 
                  : Array.isArray(recommendedResponse.products.data) 
                  ? recommendedResponse.products.data 
                  : [];
                setRecommendedProducts(recommendedList.slice(0, 8));
              }
            }
          } else if (level === 1) {
            // Subcategory - get children categories
            const categoryIdString = String(categoryId).trim();
            const children = categoriesData.filter(cat => {
              const parentId = getParentId(cat);
              if (!parentId) return false;
              return String(parentId).trim() === categoryIdString;
            });
            setChildrenCategories(children);
            setSubcategories([]);
            
            const productsList = await fetchProductsForCategoryPage(categoryId, parentCategoryIdForProducts);
            setProducts(productsList);
            
            // Load recommended products for subcategories pages (always load for level 1)
            const recommendedResponse = await productService.getProducts({ limit: 8, sort: 'createdAt', order: 'desc' });
            if (recommendedResponse.success && recommendedResponse.products) {
              const recommendedList = Array.isArray(recommendedResponse.products) 
                ? recommendedResponse.products 
                : Array.isArray(recommendedResponse.products.data) 
                ? recommendedResponse.products.data 
                : [];
              setRecommendedProducts(recommendedList.slice(0, 8));
            }
          } else {
            // Children category - only load products
            setSubcategories([]);
            setChildrenCategories([]);
            
            const productsList = await fetchProductsForCategoryPage(categoryId, parentCategoryIdForProducts);
            setProducts(productsList);
            
            // Load recommended products if no products found
            if (productsList.length === 0) {
              const recommendedResponse = await productService.getProducts({ limit: 8, sort: 'createdAt', order: 'desc' });
              if (recommendedResponse.success && recommendedResponse.products) {
                const recommendedList = Array.isArray(recommendedResponse.products) 
                  ? recommendedResponse.products 
                  : Array.isArray(recommendedResponse.products.data) 
                  ? recommendedResponse.products.data 
                  : [];
                setRecommendedProducts(recommendedList.slice(0, 8));
              }
            }
          }
        } else {
          setError('Category not found');
        }
      } catch (err) {
        console.error('Error loading category:', err);
        setError(formatCategoryLoadError(err));
      } finally {
        setLoading(false);
      }
    };

    if (searchParam.trim()) {
      loadCategoryData();
      return;
    }
    if (category || (tag && !category) || brand) {
      loadCategoryData();
    }
  }, [category, subcategory, tag, brand, searchParam, location.state?.tagLabel, location.key]);

  // Handle window resize for recommended products scrolling and main products page size
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      const newSize = isMobile ? 2 : 4;
      setRecommendedGroupSize(newSize);
      setCurrentRecommendedGroup(0);
      setProductsPerPage(isMobile ? 12 : 24);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset to first page when route or product list changes
  useEffect(() => {
    setCurrentPage(1);
  }, [category, subcategory, tag, brand, searchParam, products.length]);

  // Auto-scroll for recommended products (only if more than one group)
  useEffect(() => {
    const limitedProducts = Math.min(recommendedProducts.length, 8);
    const totalGroups = limitedProducts > 0 ? Math.ceil(limitedProducts / recommendedGroupSize) : 0;
    
    if (limitedProducts > recommendedGroupSize && totalGroups > 1) {
      const interval = setInterval(() => {
        setCurrentRecommendedGroup(prev => (prev + 1) % totalGroups);
      }, 5000); // Auto-scroll every 5 seconds
      
      return () => clearInterval(interval);
    }
  }, [recommendedProducts.length, recommendedGroupSize]);

  if (loading) {
    return (
      <div className="category-page">
        <div className="container">
          <SiteLoading
            title="Loading Category"
            subtitle="Fetching products..."
            icon="📂"
            variant="full"
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="category-page">
        <div className="container" style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.2rem', color: '#d32f2f' }}>{error}</div>
          <button 
            onClick={() => navigate('/categories')} 
            style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}
          >
            Browse All Categories
          </button>
        </div>
      </div>
    );
  }

  if (!categoryData) {
    return (
      <div className="category-page">
        <div className="container" style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.2rem', color: '#666' }}>Category not found</div>
          <button 
            onClick={() => navigate('/categories')} 
            style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}
          >
            Browse All Categories
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="category-page">
      <div
        className={`category-header${categoryData.image ? ' category-header--has-image' : ''}`}
        style={{
          backgroundImage: categoryData.image
            ? `url(${categoryData.image})`
            : 'linear-gradient(135deg, var(--primary-gold) 0%, var(--dark-gold) 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
        }}
      >
        <div
          className={`category-header-overlay${categoryData.image ? ' category-header-overlay--photo' : ''}`}
          aria-hidden
        />
        <div className="container category-header-inner">
          <div className="category-header-text">
            <h1 className="category-title">{categoryData.name}</h1>
            {categoryData.description && (
              <p className="category-subtitle">{categoryData.description}</p>
            )}
          </div>
        </div>
      </div>

      {searchParam.trim() && (
        <div className="container category-search-meta">
          <div className="category-search-meta__query">
            Search: <strong>{searchParam.trim()}</strong>
          </div>
          <div className="category-search-meta__scopes">
            <span>Name</span>
            <span>Brand</span>
            <span>Material/Fabric</span>
            <span>Tags</span>
            <span>Colors</span>
          </div>
        </div>
      )}

      <div className="container" style={{ padding: '40px 20px' }}>
        {/* Show subcategories and children categories for main category */}
        {categoryLevel === 0 && (subcategories.length > 0 || childrenCategories.length > 0) && (
          <div className="category-subcategories-section" style={{ marginBottom: '50px' }}>
            {subcategories.length > 0 && (
              <div style={{ marginBottom: '40px' }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                  gap: '25px'
                }}>
                  {subcategories.map((subcat) => {
                    const subcatSlug = getCategorySlug(subcat, allCategories);
                    return (
                      <Link 
                        key={subcat._id || subcat.id} 
                        to={`/category/${subcatSlug}`}
                        className="category-card-link"
                        style={{
                          textDecoration: 'none',
                          color: 'inherit'
                        }}
                      >
                        <div className="category-card" style={{
                          background: 'white',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                          cursor: 'pointer',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-5px)';
                          e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                        }}
                        >
                          {subcat.image && (
                            <div style={{
                              width: '100%',
                              height: '200px',
                              overflow: 'hidden',
                              background: '#f5f5f5'
                            }}>
                              <img 
                                src={subcat.image} 
                                alt={subcat.name}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  e.target.src = placeholderDataUrl(400, 300, subcat.name);
                                }}
                              />
                            </div>
                          )}
                          <div style={{ padding: '20px', flex: 1 }}>
                            <h3 style={{ 
                              fontSize: '1.3rem', 
                              margin: '0 0 10px 0',
                              color: '#333',
                              fontWeight: '600'
                            }}>
                              {subcat.name}
                            </h3>
                            {subcat.description && (
                              <p style={{ 
                                fontSize: '0.9rem', 
                                color: '#666',
                                margin: 0,
                                lineHeight: '1.5'
                              }}>
                                {subcat.description.length > 100 
                                  ? `${subcat.description.substring(0, 100)}...` 
                                  : subcat.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
            
            {childrenCategories.length > 0 && (
              <div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                  gap: '25px'
                }}>
                  {childrenCategories.map((childCat) => {
                    const childSlug = getCategorySlug(childCat, allCategories);
                    return (
                      <Link 
                        key={childCat._id || childCat.id} 
                        to={`/category/${childSlug}`}
                        className="category-card-link"
                        style={{
                          textDecoration: 'none',
                          color: 'inherit'
                        }}
                      >
                        <div className="category-card" style={{
                          background: 'white',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                          cursor: 'pointer',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-5px)';
                          e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                        }}
                        >
                          {childCat.image && (
                            <div style={{
                              width: '100%',
                              height: '200px',
                              overflow: 'hidden',
                              background: '#f5f5f5'
                            }}>
                              <img 
                                src={childCat.image} 
                                alt={childCat.name}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  e.target.src = placeholderDataUrl(400, 300, childCat.name);
                                }}
                              />
                            </div>
                          )}
                          <div style={{ padding: '20px', flex: 1 }}>
                            <h3 style={{ 
                              fontSize: '1.3rem', 
                              margin: '0 0 10px 0',
                              color: '#333',
                              fontWeight: '600'
                            }}>
                              {childCat.name}
                            </h3>
                            {childCat.description && (
                              <p style={{ 
                                fontSize: '0.9rem', 
                                color: '#666',
                                margin: 0,
                                lineHeight: '1.5'
                              }}>
                                {childCat.description.length > 100 
                                  ? `${childCat.description.substring(0, 100)}...` 
                                  : childCat.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Show children categories for subcategory */}
        {categoryLevel === 1 && childrenCategories.length > 0 && (
          <div className="category-children-section" style={{ marginBottom: '50px' }}>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '25px', color: '#333', fontWeight: '600' }}>
              Children Categories
            </h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
              gap: '25px'
            }}>
              {childrenCategories.map((childCat) => {
                const childSlug = getCategorySlug(childCat, allCategories);
                return (
                  <Link 
                    key={childCat._id || childCat.id} 
                    to={`/category/${childSlug}`}
                    className="category-card-link"
                    style={{
                      textDecoration: 'none',
                      color: 'inherit'
                    }}
                  >
                    <div className="category-card" style={{
                      background: 'white',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                    }}
                    >
                      {childCat.image && (
                        <div style={{
                          width: '100%',
                          height: '200px',
                          overflow: 'hidden',
                          background: '#f5f5f5'
                        }}>
                          <img 
                            src={childCat.image} 
                            alt={childCat.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.src = placeholderDataUrl(400, 300, childCat.name);
                            }}
                          />
                        </div>
                      )}
                      <div style={{ padding: '20px', flex: 1 }}>
                        <h3 style={{ 
                          fontSize: '1.3rem', 
                          margin: '0 0 10px 0',
                          color: '#333',
                          fontWeight: '600'
                        }}>
                          {childCat.name}
                        </h3>
                        {childCat.description && (
                          <p style={{ 
                            fontSize: '0.9rem', 
                            color: '#666',
                            margin: 0,
                            lineHeight: '1.5'
                          }}>
                            {childCat.description.length > 100 
                              ? `${childCat.description.substring(0, 100)}...` 
                              : childCat.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Show products for all category levels (paginated: 12 / mobile, 24 / desktop) */}
        {products.length > 0 && (() => {
          const totalPages = Math.max(1, Math.ceil(products.length / productsPerPage));
          const safePage = Math.min(Math.max(1, currentPage), totalPages);
          const startIndex = (safePage - 1) * productsPerPage;
          const paginatedProducts = products.slice(startIndex, startIndex + productsPerPage);

          // Build a compact page-number list with ellipses (1, …, p-1, p, p+1, …, last)
          const pagesToShow = (() => {
            const set = new Set([1, totalPages, safePage]);
            for (let d = 1; d <= 2; d += 1) {
              if (safePage - d >= 1) set.add(safePage - d);
              if (safePage + d <= totalPages) set.add(safePage + d);
            }
            return Array.from(set).sort((a, b) => a - b);
          })();

          const goToPage = (page) => {
            const target = Math.min(Math.max(1, page), totalPages);
            if (target === safePage) return;
            setCurrentPage(target);
            const grid = document.querySelector('.category-products-grid');
            if (grid && typeof grid.scrollIntoView === 'function') {
              grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          };

          return (
            <div style={{ marginTop: categoryLevel === 2 ? '0' : '50px' }}>
              <h2 style={{ fontSize: '1.8rem', marginBottom: '25px', color: '#333', fontWeight: '600' }}>
                Products
              </h2>
              <div className="category-products-grid">
                {paginatedProducts.map((product) => (
                  <ProductCard key={product._id || product.id} product={product} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>Page {safePage} of {totalPages}</span>
                    <span>•</span>
                    <span>
                      Showing {startIndex + 1}
                      {paginatedProducts.length > 1 ? `–${startIndex + paginatedProducts.length}` : ''} of {products.length}
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button
                      type="button"
                      className={`pagination-btn ${safePage === 1 ? 'disabled' : ''}`}
                      onClick={() => goToPage(safePage - 1)}
                      disabled={safePage === 1}
                      aria-label="Previous page"
                    >
                      <i className="fas fa-chevron-left" /> Prev
                    </button>
                    {pagesToShow.map((p, idx) => {
                      const prev = pagesToShow[idx - 1];
                      const showEllipsis = idx > 0 && p - prev > 1;
                      return (
                        <React.Fragment key={p}>
                          {showEllipsis && (
                            <span className="pagination-btn disabled" aria-hidden style={{ cursor: 'default' }}>
                              …
                            </span>
                          )}
                          <button
                            type="button"
                            className={`pagination-btn ${p === safePage ? 'active' : ''}`}
                            onClick={() => goToPage(p)}
                            aria-current={p === safePage ? 'page' : undefined}
                          >
                            {p}
                          </button>
                        </React.Fragment>
                      );
                    })}
                    <button
                      type="button"
                      className={`pagination-btn ${safePage === totalPages ? 'disabled' : ''}`}
                      onClick={() => goToPage(safePage + 1)}
                      disabled={safePage === totalPages}
                      aria-label="Next page"
                    >
                      Next <i className="fas fa-chevron-right" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        
        {/* Show scrolling "You may also like" section on main categories and subcategories pages */}
        {recommendedProducts.length > 0 && (categoryLevel === 0 || categoryLevel === 1) && (
          <div className={relatedSectionClassName} style={{ marginTop: '50px' }}>
            <div className={relatedContainerClassName}>
              <div className="section-title-wrapper">
                <h2 className="professional-title">You May Also Like</h2>
                <div className="title-underline"></div>
              </div>
              
              <div className={relatedGridClassName}>
                {(() => {
                  const limitedProducts = recommendedProducts.slice(0, 8);
                  const startIndex = currentRecommendedGroup * recommendedGroupSize;
                  const currentProducts = limitedProducts.slice(startIndex, startIndex + recommendedGroupSize);
                  
                  return currentProducts.map((product) => {
                    if (!product || typeof product !== 'object') {
                      return null;
                    }
                    
                    const productForCard = {
                      id: product.id || product._id,
                      _id: product._id || product.id,
                      name: product.name || 'Product',
                      price: product.price || 0,
                      originalPrice: product.originalPrice || product.original_price || null,
                      salePrice: product.salePrice || product.sale_price || null,
                      images: product.images || (product.image ? [product.image] : []),
                      image: getPrimaryProductImageUrl(product),
                      url: product.url || `product/${product.id || product._id}`,
                      isNew: product.isNew || product.is_new || false,
                      isOnSale: product.isOnSale || product.is_on_sale || false,
                      isFeatured: product.isFeatured || product.is_featured || false,
                      category: typeof product.category === 'string' ? product.category : (product.category?.name || product.category?.slug || 'general'),
                      colors: product.colors || [],
                      sizes: product.sizes || [],
                      stock: product.stock || {},
                      description: product.description || '',
                      brand: product.brand || ''
                    };
                    
                    return (
                      <ProductCard
                        key={productForCard.id || productForCard._id}
                        product={productForCard}
                      />
                    );
                  });
                })()}
              </div>
              
              {(() => {
                const limitedProducts = Math.min(recommendedProducts.length, 8);
                const totalGroups = limitedProducts > 0 ? Math.ceil(limitedProducts / recommendedGroupSize) : 0;
                
                if (totalGroups > 1) {
                  return (
                    <div className="showcase-navigation">
                      <button 
                        className="nav-arrow prev"
                        onClick={() => setCurrentRecommendedGroup(prev => prev === 0 ? totalGroups - 1 : prev - 1)}
                        title="Previous Products"
                      >
                        <i className="fas fa-chevron-left"></i>
                      </button>
                      <div className="nav-dots">
                        {Array.from({length: totalGroups}, (_, index) => (
                          <button
                            key={index}
                            className={`nav-dot ${currentRecommendedGroup === index ? 'active' : ''}`}
                            onClick={() => setCurrentRecommendedGroup(index)}
                          />
                        ))}
                      </div>
                      <button 
                        className="nav-arrow next"
                        onClick={() => setCurrentRecommendedGroup(prev => prev === totalGroups - 1 ? 0 : prev + 1)}
                        title="Next Products"
                      >
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        )}
        
        {/* Show scrolling "You may also like" section when no products and no children categories (fallback) */}
        {products.length === 0 && recommendedProducts.length > 0 && (
          <>
            {(categoryLevel === 0 || categoryLevel === 1) && subcategories.length === 0 && childrenCategories.length === 0 && (
              <div className={relatedSectionClassName} style={{ marginTop: '50px' }}>
                <div className={relatedContainerClassName}>
                  <div className="section-title-wrapper">
                    <h2 className="professional-title">You May Also Like</h2>
                    <div className="title-underline"></div>
                  </div>
                  
                  <div className={relatedGridClassName}>
                    {(() => {
                      const limitedProducts = recommendedProducts.slice(0, 8);
                      const startIndex = currentRecommendedGroup * recommendedGroupSize;
                      const currentProducts = limitedProducts.slice(startIndex, startIndex + recommendedGroupSize);
                      
                      return currentProducts.map((product) => {
                        if (!product || typeof product !== 'object') {
                          return null;
                        }
                        
                        const productForCard = {
                          id: product.id || product._id,
                          _id: product._id || product.id,
                          name: product.name || 'Product',
                          price: product.price || 0,
                          originalPrice: product.originalPrice || product.original_price || null,
                          salePrice: product.salePrice || product.sale_price || null,
                          images: product.images || (product.image ? [product.image] : []),
                          image: getPrimaryProductImageUrl(product),
                          url: product.url || `product/${product.id || product._id}`,
                          isNew: product.isNew || product.is_new || false,
                          isOnSale: product.isOnSale || product.is_on_sale || false,
                          isFeatured: product.isFeatured || product.is_featured || false,
                          category: typeof product.category === 'string' ? product.category : (product.category?.name || product.category?.slug || 'general'),
                          colors: product.colors || [],
                          sizes: product.sizes || [],
                          stock: product.stock || {},
                          description: product.description || '',
                          brand: product.brand || ''
                        };
                        
                        return (
                          <ProductCard
                            key={productForCard.id || productForCard._id}
                            product={productForCard}
                          />
                        );
                      });
                    })()}
                  </div>
                  
                  {(() => {
                    const limitedProducts = Math.min(recommendedProducts.length, 8);
                    const totalGroups = limitedProducts > 0 ? Math.ceil(limitedProducts / recommendedGroupSize) : 0;
                    
                    if (totalGroups > 1) {
                      return (
                        <div className="showcase-navigation">
                          <button 
                            className="nav-arrow prev"
                            onClick={() => setCurrentRecommendedGroup(prev => prev === 0 ? totalGroups - 1 : prev - 1)}
                            title="Previous Products"
                          >
                            <i className="fas fa-chevron-left"></i>
                          </button>
                          <div className="nav-dots">
                            {Array.from({length: totalGroups}, (_, index) => (
                              <button
                                key={index}
                                className={`nav-dot ${currentRecommendedGroup === index ? 'active' : ''}`}
                                onClick={() => setCurrentRecommendedGroup(index)}
                              />
                            ))}
                          </div>
                          <button 
                            className="nav-arrow next"
                            onClick={() => setCurrentRecommendedGroup(prev => prev === totalGroups - 1 ? 0 : prev + 1)}
                            title="Next Products"
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            )}
            
            {categoryLevel === 2 && (
              <div className={relatedSectionClassName} style={{ marginTop: '50px' }}>
                <div className={relatedContainerClassName}>
                  <div className="section-title-wrapper">
                    <h2 className="professional-title">You May Also Like</h2>
                    <div className="title-underline"></div>
                  </div>
                  
                  <div className={relatedGridClassName}>
                    {(() => {
                      const limitedProducts = recommendedProducts.slice(0, 8);
                      const startIndex = currentRecommendedGroup * recommendedGroupSize;
                      const currentProducts = limitedProducts.slice(startIndex, startIndex + recommendedGroupSize);
                      
                      return currentProducts.map((product) => {
                        if (!product || typeof product !== 'object') {
                          return null;
                        }
                        
                        const productForCard = {
                          id: product.id || product._id,
                          _id: product._id || product.id,
                          name: product.name || 'Product',
                          price: product.price || 0,
                          originalPrice: product.originalPrice || product.original_price || null,
                          salePrice: product.salePrice || product.sale_price || null,
                          images: product.images || (product.image ? [product.image] : []),
                          image: getPrimaryProductImageUrl(product),
                          url: product.url || `product/${product.id || product._id}`,
                          isNew: product.isNew || product.is_new || false,
                          isOnSale: product.isOnSale || product.is_on_sale || false,
                          isFeatured: product.isFeatured || product.is_featured || false,
                          category: typeof product.category === 'string' ? product.category : (product.category?.name || product.category?.slug || 'general'),
                          colors: product.colors || [],
                          sizes: product.sizes || [],
                          stock: product.stock || {},
                          description: product.description || '',
                          brand: product.brand || ''
                        };
                        
                        return (
                          <ProductCard
                            key={productForCard.id || productForCard._id}
                            product={productForCard}
                          />
                        );
                      });
                    })()}
                  </div>
                  
                  {(() => {
                    const limitedProducts = Math.min(recommendedProducts.length, 8);
                    const totalGroups = limitedProducts > 0 ? Math.ceil(limitedProducts / recommendedGroupSize) : 0;
                    
                    if (totalGroups > 1) {
                      return (
                        <div className="showcase-navigation">
                          <button 
                            className="nav-arrow prev"
                            onClick={() => setCurrentRecommendedGroup(prev => prev === 0 ? totalGroups - 1 : prev - 1)}
                            title="Previous Products"
                          >
                            <i className="fas fa-chevron-left"></i>
                          </button>
                          <div className="nav-dots">
                            {Array.from({length: totalGroups}, (_, index) => (
                              <button
                                key={index}
                                className={`nav-dot ${currentRecommendedGroup === index ? 'active' : ''}`}
                                onClick={() => setCurrentRecommendedGroup(index)}
                              />
                            ))}
                          </div>
                          <button 
                            className="nav-arrow next"
                            onClick={() => setCurrentRecommendedGroup(prev => prev === totalGroups - 1 ? 0 : prev + 1)}
                            title="Next Products"
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            )}
          </>
        )}
        
        {/* Show empty state when no products and no recommended products */}
        {products.length === 0 && recommendedProducts.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666', marginTop: '50px' }}>
            <p style={{ fontSize: '1.2rem', marginBottom: '20px' }}>
              {categoryLevel === 0 
                ? 'No subcategories, children categories, or products found.' 
                : categoryLevel === 1
                ? 'No children categories or products found.'
                : 'No products found in this category.'}
            </p>
            <button 
              onClick={() => navigate('/categories')} 
              style={{ padding: '10px 20px', cursor: 'pointer' }}
            >
              Browse All Categories
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;

